import { useScreenLock } from "@/components/ScreenLock";
import {
  EMAIL_ADDRESS_REGEX,
  PASSWORD_SYMBOL_REGEX,
} from "@/components/activation/Constants";
import { NonFieldError } from "@/components/common/SCAlert";
import { ScrollToTop } from "@/components/common/ScrollToTop";
import { postProjectEntry } from "@/components/examinee/participation/ParticipationAPI";
import { GeneralError } from "@/components/sleep_checkup_v1/GeneralError";
import { BirthdayForm, TextForm } from "@/components/sleep_checkup_v1/TextForm";
import { validateBirthday } from "@/utils/date";
import { PasswordCheckListItem } from "@/pages/examinee/activation/SignUp";
import { StateType as ProjectConfirmationStateType } from "@/pages/examinee/participation/ProjectConfirmation";
import { Button, LinearProgress, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * プロジェクト固有 URL からの新規アカウント登録ページ。
 * 姓・名・生年月日・メールアドレス・パスワードを入力し、
 * 確認コード送信（POST /api/participation/project_entry/）を行う。
 * 姓・名・生年月日は事前 CSV 登録済みレコードとの照合に使う（受付番号の入力は廃止）。
 * 保存される氏名・カナ・生年月日は事前レコードの値で、入力値は照合のみに使われる。
 */
export const ProjectSignUp = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const urlId = new URLSearchParams(location.search).get("url_id");

  const [errorMessage, setErrorMessage] = useState("");
  const [emailErrorMessages, setEmailErrorMessages] = useState<string[]>([]);
  const [passwordErrorMessages, setPasswordErrorMessages] = useState<string[]>(
    []
  );
  const [lastNameErrorMessages, setLastNameErrorMessages] = useState<string[]>(
    []
  );
  const [firstNameErrorMessages, setFirstNameErrorMessages] = useState<
    string[]
  >([]);
  const [birthdayErrorMessages, setBirthdayErrorMessages] = useState<string[]>(
    []
  );
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  /** BirthdayForm と同じ表記（入力中は "YYYYMMDD"、確定後は "YYYY/MM/DD"） */
  const [birthday, setBirthday] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLocked, setLock] = useScreenLock();

  if (!urlId) {
    return <GeneralError to="" />;
  }

  const validators: Record<string, () => string[] | null> = {
    lastName: () => (lastName.trim() ? null : ["※姓を入力してください"]),
    firstName: () => (firstName.trim() ? null : ["※名を入力してください"]),
    birthday: () => validateBirthday(birthday.split("/").join("")),
    email: () => {
      const errors: string[] = [];
      if (!email.match(EMAIL_ADDRESS_REGEX)) {
        errors.push("※メールアドレスの形式が正しくありません");
      }
      if (email.length > 254) {
        errors.push("※メールアドレスは254文字以下にしてください");
      }
      return errors.length ? errors : null;
    },
    passwordLength: () => {
      if (password.length < 8 || 24 < password.length) {
        return ["NG"]; // この返り値は使われない
      }
      return null;
    },
    passwordAlphabetLowerAndUpper: () => {
      if (!(password.match(/[a-z]/) && password.match(/[A-Z]/))) {
        return ["NG"]; // この返り値は使われない
      }
      return null;
    },
    passwordDigit: () => {
      if (!password.match(/\d/)) {
        return ["NG"]; // この返り値は使われない
      }
      return null;
    },
    passwordSymbol: () => {
      if (!password.match(PASSWORD_SYMBOL_REGEX)) {
        return ["NG"]; // この返り値は使われない
      }
      return null;
    },
    passwordInvalidChars: () => {
      if (
        password.replace(PASSWORD_SYMBOL_REGEX, "").replace(/[a-zA-Z\d]/g, "")
      ) {
        return ["※使用できない文字が含まれています"];
      }
      return null;
    },
  };

  const handleClickRegister = () => {
    if (isLocked) {
      // 画面ロック中の場合、登録処理を行わない
      return;
    }
    // 前回送信時のサーバエラーをクリアする。表示先は応答ごとに 1 箇所に絞るため、
    // フィールドエラーと NonFieldError が同時に残留しないようにする
    // （クライアント側バリデーションで弾かれる場合も旧エラーを残さない）
    setErrorMessage("");
    setLastNameErrorMessages([]);
    setFirstNameErrorMessages([]);
    setBirthdayErrorMessages([]);
    setEmailErrorMessages([]);
    setPasswordErrorMessages([]);
    if (Object.values(validators).some((v) => v())) {
      // 何かしらのエラーがある場合は登録処理を行わない
      return;
    }
    setLock(true);
    // BirthdayForm の確定表記 "YYYY/MM/DD" を API の "YYYY-MM-DD" に変換する
    const birthdayWithHyphen = birthday.replace(
      /(\d{4})\/(\d{2})\/(\d{2})/,
      "$1-$2-$3"
    );
    // 前後の空白は送信前に除く（内部空白・全角/半角の差は BE 側の正規化で吸収される）
    const trimmedLastName = lastName.trim();
    const trimmedFirstName = firstName.trim();
    postProjectEntry(
      email,
      password,
      urlId,
      trimmedLastName,
      trimmedFirstName,
      birthdayWithHyphen
    )
      .then((res) => {
        navigate("/examinee/project_confirmation", {
          state: {
            urlId,
            email,
            password,
            lastName: trimmedLastName,
            firstName: trimmedFirstName,
            birthdayWithHyphen,
            entryId: res.data.id,
          } as ProjectConfirmationStateType,
        });
      })
      .catch((err) => {
        setLock(false); // エラー時はロックを解放する。
        const statusCode = err.response?.status;
        const messages = err.response?.data?.error_messages;
        const lastNameErrors = err.response?.data?.last_name;
        const firstNameErrors = err.response?.data?.first_name;
        const birthdayErrors = err.response?.data?.birthday;
        let isKnownError = false;
        // 「形式不正（DRF のフィールドエラー）→ 照合失敗（error_code）→ その他」の
        // 順に判定し、表示先を 1 箇所に絞る。
        // 照合失敗は姓・名・生年月日のどれが原因かも含めて区別されず同一の
        // error_code で返り、BE は error_messages も同時に含めるため、else-if にして
        // 二重表示を避ける（ProjectJoin と分岐順序を揃えている）。
        // 照合失敗は 3 フィールドに跨がるためページ上部の NonFieldError に表示する
        if (lastNameErrors || firstNameErrors || birthdayErrors) {
          isKnownError = true;
          if (lastNameErrors) {
            setLastNameErrorMessages(lastNameErrors);
          }
          if (firstNameErrors) {
            setFirstNameErrorMessages(firstNameErrors);
          }
          if (birthdayErrors) {
            setBirthdayErrorMessages(birthdayErrors);
          }
        } else if (
          err.response?.data?.error_code === "examinee_identity_mismatch"
        ) {
          isKnownError = true;
          setErrorMessage(
            "氏名または生年月日が確認できません。実施企業・団体に登録されているお名前と生年月日をご確認ください"
          );
        } else if (messages && messages.length > 0) {
          isKnownError = true;
          setErrorMessage(messages[0]);
        }

        const urlIdErrors = err.response?.data?.url_id;
        if (urlIdErrors && urlIdErrors.length > 0) {
          isKnownError = true;
          setErrorMessage(urlIdErrors[0]);
        }
        const emailErrors = err.response?.data?.email;
        if (emailErrors) {
          isKnownError = true;
          setEmailErrorMessages(emailErrors);
        }
        const passwordErrors = err.response?.data?.password;
        if (passwordErrors) {
          isKnownError = true;
          setPasswordErrorMessages(passwordErrors);
        }
        if (
          statusCode &&
          400 <= statusCode &&
          statusCode < 500 &&
          !isKnownError
        ) {
          setErrorMessage(
            "エラーが発生しました。申し訳ありませんが、少し時間をおいてから再度お試しください。"
          );
        }

        // エラーアラートが画面内に表示されるようにスクロールをリセットする
        window.scrollTo(0, 0);
      });
  };

  return (
    <>
      <ScrollToTop />
      <LinearProgress
        variant="determinate"
        value={(2 / 6) * 100}
        sx={{
          borderRadius: 1,
          my: 6,
          mx: 4,
          height: "6px",
        }}
      />
      <Stack spacing={8} sx={{ mt: 8, mb: 12, mx: 4 }}>
        <Stack spacing={6}>
          <Typography variant="h6" align="center">
            新規アカウント登録
          </Typography>

          {/* エラーメッセージ */}
          <NonFieldError>{errorMessage}</NonFieldError>

          <Typography
            variant="subtitle2"
            textAlign="justify"
            sx={{ color: "text.secondary" }}
          >
            以下のフォームに姓、名、生年月日、登録確認用メールを受信できるメールアドレス、ログイン時に使用するパスワードを入力し、[登録する]ボタンを押してください。
          </Typography>

          <TextForm
            title="姓"
            placeholder="例：睡眠"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setLastNameErrorMessages([]);
            }}
            validator={validators.lastName}
            helperText="※実施企業・団体に登録されているお名前（姓）を入力してください"
            givenErrors={lastNameErrorMessages}
          />
          <TextForm
            title="名"
            placeholder="例：太郎"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setFirstNameErrorMessages([]);
            }}
            validator={validators.firstName}
            helperText="※実施企業・団体に登録されているお名前（名）を入力してください"
            givenErrors={firstNameErrorMessages}
          />
          <Stack spacing={1}>
            <Typography variant="subtitle2">生年月日</Typography>
            <BirthdayForm
              value={birthday}
              givenErrors={birthdayErrorMessages}
              birthdaySetAction={(value) => {
                setBirthday(value);
                setBirthdayErrorMessages([]);
              }}
              birthdayValidator={validators.birthday}
            />
          </Stack>
          <TextForm
            title="メールアドレス"
            placeholder="例：sleep-compass@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            validator={validators.email}
            helperText="※測定期間中(休日を含む)、毎日確認ができるメールアドレスを入力してください"
            givenErrors={emailErrorMessages}
          />
          <TextForm
            title="新しいパスワード"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
            }}
            type="password"
            helperText={
              <Stack>
                <PasswordCheckListItem
                  text="8文字以上24文字以下にしてください"
                  validator={validators.passwordLength}
                />
                <PasswordCheckListItem
                  text="大文字と小文字の半角英字を含めてください"
                  validator={validators.passwordAlphabetLowerAndUpper}
                />
                <PasswordCheckListItem
                  text="半角数字を含めてください"
                  validator={validators.passwordDigit}
                />
                <PasswordCheckListItem
                  text="記号を含めてください"
                  validator={validators.passwordSymbol}
                />
              </Stack>
            }
            validator={validators.passwordInvalidChars}
            givenErrors={passwordErrorMessages}
          />
        </Stack>
      </Stack>
      <Stack>
        <Button
          variant="contained"
          size="large"
          sx={{ mx: 4, mb: 16 }}
          onClick={handleClickRegister}
          disabled={Object.values(validators).some((v) => v()) || isLocked}
        >
          登録する
        </Button>
      </Stack>
    </>
  );
};
